import React from 'react';
import { useLocation } from 'react-router-dom';
import { addTrailingSlash, getPathnameFromURI } from 'Helpers/utils';
import { SettingsGroup, SettingsItem, SETTINGS_GROUPS } from './constants';

const SETTINGS_ITEMS: SettingsItem[] = SETTINGS_GROUPS.flatMap(group => group.items);

const GROUP_BY_ITEM_ID: Record<string, SettingsGroup> = SETTINGS_GROUPS.reduce<
  Record<string, SettingsGroup>
>((acc, group) => {
  group.items.forEach(item => {
    acc[item.id] = group;
  });
  return acc;
}, {});

export interface ActiveSettingsItem {
  item: SettingsItem;
  group: SettingsGroup;
}

interface Candidate {
  itemPath: string;
  section: string | null;
}

const itemCandidates = (item: SettingsItem): Candidate[] => {
  const sources = [item.to, ...(item.activePrefixes ?? [])];
  return sources.map(source => {
    const [path, query] = source.split('?');
    return {
      itemPath: addTrailingSlash(getPathnameFromURI(path)),
      section: query ? new URLSearchParams(query).get('section') : null,
    };
  });
};

/**
 * Returns the sidebar item that should appear active for the current
 * location plus its enclosing group. Picks the longest matching
 * destination so nested routes (e.g. `/settings/roles/new`) resolve to
 * their parent ("Roles & Permissions") rather than to a less-specific
 * sibling. Returns `null` when no item matches — callers must handle that
 * (typically by omitting the page header).
 */
export function useActiveSettingsItem(): ActiveSettingsItem | null {
  const location = useLocation();
  const currentPath = addTrailingSlash(location.pathname);
  const currentSection = new URLSearchParams(location.search).get('section');

  return React.useMemo(() => {
    const matches = SETTINGS_ITEMS.flatMap(item =>
      itemCandidates(item)
        .filter(candidate => currentPath.startsWith(candidate.itemPath))
        .filter(candidate => !candidate.section || candidate.section === currentSection)
        // Score: prefer longer path matches, then matches that explicitly
        // pin a section query param (so two items at the same path are
        // disambiguated by the section).
        .map(candidate => ({
          item,
          score: candidate.itemPath.length + (candidate.section ? 1 : 0),
        })),
    );
    if (matches.length === 0) {
      return null;
    }
    const winner = matches.reduce((best, current) => (current.score > best.score ? current : best));
    return { item: winner.item, group: GROUP_BY_ITEM_ID[winner.item.id] };
  }, [currentPath, currentSection]);
}
